import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Building2, FileText, FlaskConical } from 'lucide-react';
import Header from './Header';
import { apiService } from '../services/api';
import { EmergingCompanyAssetsResponse } from '../types';

const EmergingCompanyAssetsPage: React.FC = () => {
  const { companyId } = useParams();
  const [data, setData] = useState<EmergingCompanyAssetsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!companyId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const response = await apiService.fetchCompanyEmergingAssets(Number(companyId));
        setData(response);
        if (!response) {
          setError('Company assets not found.');
        }
      } catch (err) {
        setError('Failed to load company assets.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [companyId]);

  const totalAssets = data?.indications?.reduce((acc, g) => acc + g.assets.length, 0) ?? 0;

  return (
    <div className="min-h-screen text-slate-900 pb-20 relative bg-slate-50 font-sans">
      <Header />
      <main className="w-full max-w-[100%] mx-auto px-3 sm:px-4 lg:px-6 py-10 space-y-8">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : error ? (
          <div className="bg-white rounded-[2rem] shadow-card border border-gray-100 p-8">
            <p className="text-slate-500">{error}</p>
          </div>
        ) : !data ? (
          <div className="bg-white rounded-[2rem] shadow-card border border-gray-100 p-8">
            <p className="text-slate-500">Company not found.</p>
          </div>
        ) : (
          <>
            <section className="bg-white rounded-[2rem] shadow-card border border-gray-100 p-8">
              <div className="flex flex-col sm:flex-row justify-between gap-6">
                <div>
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Building2 className="w-4 h-4 text-indigo-500" />
                    <span>Company</span>
                  </div>
                  <h1 className="text-3xl font-extrabold text-slate-900 mt-2">{data.company_name}</h1>
                  <p className="text-sm text-slate-500 mt-2">
                    Assets in development by indication. Each indication links to an Emerging Report.
                  </p>
                </div>
                <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                  <span>{data.indications.length} indication{data.indications.length === 1 ? '' : 's'}</span>
                  <span>{totalAssets} asset{totalAssets === 1 ? '' : 's'}</span>
                </div>
              </div>
            </section>

            <section className="bg-white rounded-[2rem] shadow-card border border-gray-100 p-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Indications, Assets & Emerging Reports</h2>
              {data.indications.length === 0 ? (
                <p className="text-slate-500 text-sm">No assets found for this company.</p>
              ) : (
                <div className="space-y-6">
                  {data.indications.map((group) => (
                    <div key={group.indication_code} className="border border-slate-100 rounded-2xl p-5">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <h3 className="text-lg font-semibold text-slate-900">{group.indication_name}</h3>
                        {group.report_url ? (
                          <Link
                            to={group.report_url}
                            className="inline-flex items-center gap-2 rounded-lg bg-indigo-50 px-3 py-1.5 text-sm font-medium text-indigo-700 hover:bg-indigo-100"
                          >
                            <FileText className="w-4 h-4" />
                            View Emerging Report
                          </Link>
                        ) : null}
                      </div>
                      <div className="mt-4">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Assets</p>
                        <ul className="space-y-2">
                          {group.assets.map((asset) => (
                            <li key={asset.id}>
                              <Link
                                to={`/emerging-assets/${asset.id}`}
                                className="inline-flex items-center gap-2 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-900 hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-100 w-full max-w-md"
                              >
                                <FlaskConical className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                                <span className="truncate">{asset.asset_name}</span>
                                {asset.status && (
                                  <span className="ml-auto text-[11px] font-normal text-slate-500">{asset.status}</span>
                                )}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
};

export default EmergingCompanyAssetsPage;
